import { CompositeScreenProps } from "@react-navigation/native"
import { observer } from "mobx-react-lite"
import { View } from "native-base"
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Appearance, FlatList, ImageBackground, ImageStyle, TextStyle, TouchableOpacity, ViewStyle } from "react-native"
import { AutoImage, JalaLoading, LazyRender, Screen, SearchBar, Text } from "../../../components"
import { translate } from "../../../i18n"
import { useStores } from "../../../models"
import { navigate } from "../../../navigators"
import { color } from "../../../theme"
import config from "../../../utils/config"
import { EmptyState } from "../disease/disease-screen"
import JalaAnalytic from "../../../utils/jala-analytic"
import { delay } from "../../../utils/delay"

const ROOT: ViewStyle = {
  flex: 1,
}


const SEARCH_CONTAINER: ViewStyle = {
  paddingHorizontal: 16,
  marginTop: 16
}

const TITLE_TEXT: TextStyle = {
  paddingBottom: 6,
  paddingHorizontal: 16,
  marginTop: 22
}

const CONTAINER: ViewStyle = {
  flex: 1
}

const LATEST_NEWS_CONTAINER: ViewStyle = {
  marginTop: 6,
  marginBottom: 10,
  width: 313,
  height: 225,
  borderRadius: 8,
  backgroundColor: color.palette.gray500,
}

const IMG_BACKGROUND: ImageStyle = {
  flex: 1,
  paddingHorizontal: 16,
  opacity: 0.9,
  borderRadius: 8,
  backgroundColor: color.palette.black,
  paddingBottom: 24,
  justifyContent: 'flex-end'
}

const IMG_STYLE: ImageStyle = {
  borderRadius: 8,
  resizeMode: 'cover'
}

const NEWS_TEXT: TextStyle = {
  color: color.palette.white
}

const CATEGORY_CONTAINER = (isActive: boolean): ViewStyle => ({
  backgroundColor: isActive ? color.primary : color.palette.white,
  borderWidth: 1,
  borderColor: color.palette.gray200,
  borderRadius: 38,
  paddingVertical: 6,
  paddingHorizontal: 16,
  alignItems: 'center'
})

const CATEGORY_TEXT = (isActive: boolean): TextStyle => ({
  color: isActive ? color.palette.white : color.palette.gray600
})

const CARD_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  borderWidth: 1,
  borderColor: color.palette.gray200,
  borderRadius: 8,
  marginVertical: 8
}

const NEWS_WRAPPER: ViewStyle = {
  flex: 1,
  paddingLeft: 12,
  paddingTop: 8,
  paddingBottom: 12,
  paddingRight: 8,
  justifyContent: 'space-between'
}

const AUTHOR_TEXT: TextStyle = {
  color: color.palette.gray700,
  marginTop: 4
}

const IMG: ImageStyle = {
  width: 90,
  height: 90,
  borderTopLeftRadius: 8,
  borderBottomLeftRadius: 8,
}

const LIST: ViewStyle = {
  paddingBottom: 24,
  paddingTop: 6
  // paddingVertical: 24,
}

const WIDTH: ViewStyle = {
  width: 16
}

const PADDING: ViewStyle = {
  paddingHorizontal: 16
}

const { BASE_URL } = config();


export const ShrimpNewsScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function ShrimpNewsScreen(props) {
  // Pull in one of our MST stores
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { tokenStore: { token }, newsStore, newsStore: { error, news, latestNews, categories, link, loading } } = useStores()

  const [ state, setState ] = useState({
    category: '',
    page: 1,
    full_name: '',
    title: '',
    isActiveSlug: null,
  });
  const [isActive, setActive] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const categoryRef = useRef<FlatList>()

  const activePrevRef = useRef(null);

  const isNext = useMemo(() => link?.next !== null, [link?.next])

  const params = useMemo<any>(() => {
    return {
      with: 'author',
      sort: 'created_at,desc',
      per_page: 10,
      status: 'PUBLISHED',
      category: state.isActiveSlug,
      page: state.page,
      full_name: "",
      title: "",
    }
  }, [state.isActiveSlug, state.page])

  const label = useMemo(() => {
    return isActive ? translate("newsScreen.search", { search: `${search}` }) : translate("newsScreen.latestNews");
  }, [isActive, search])

  useEffect(() => {
    newsStore.fetchCategories(null)
  },[newsStore])

  useEffect(() => {
    if (!isActive) {
      if (state.page > 1 && isNext) {
        newsStore.fetchNews(token.access_token, params, state.page)
      } else if (activePrevRef !== state.isActiveSlug && state.page === 1 && !newsStore.loading) {
        newsStore.setNews([]);
        newsStore.fetchNews(token.access_token, params, 1);
      }
    }
  }, [state.page, state.isActiveSlug, params, isActive, newsStore, token.access_token, isNext, state, categories])

  useEffect(() => {
    if (activePrevRef !== state.isActiveSlug && state.isActiveSlug && !newsStore.loading) {
      const index = categories.findIndex(item => item.slug === state.isActiveSlug);
      delay(500).then(() => {
        categoryRef?.current?.scrollToIndex({index: index});
      })
    }
  }, [categories, state.isActiveSlug, newsStore.loading])

  const filter = useCallback((slug) => {
    activePrevRef.current = state.isActiveSlug;
    setActive(false);
    setSearch("")
    setState({...state, isActiveSlug: slug, page: 1})
  },[state])

  const next = useCallback(() => {
    !newsStore.loading && setState({ ...state, page: state.page + 1 })
  }, [state, newsStore.loading])

  const onDetail = useCallback((data) => () => {
    JalaAnalytic.log('Mobile app: visit detail news', { URL: 'NewsList' });
    navigate("Home.Stack", "Home.News.Detail", { data })
    newsStore.setStateNews(data)
  },[newsStore])

  const onRefresh = useCallback(() => {
    newsStore.fetchNews(null, params, 1)
    newsStore.fetchCategories(null)
    setState({...state, page: 1})
    newsStore.setError(null);
  }, [newsStore, params, state])

  const handleSearch = () => {
    const params = {
      with: 'author', sort: 'created_at,desc', status: 'PUBLISHED', per_page: 10, page: 1
    };
    if (search.length !== 0) {
      if (!isActive) {
        setState({
          ...state,
          page: 1
        })
        newsStore.set('news', [])
        newsStore.searchNews(search, params);
        setActive(!!search.length);
      }
      else {
        setActive(false);
        setSearch("");
        setState({
          ...state,
          page: 1
        });
      }
    }
  }

  const handleChangeText = (value: string) => {
    setSearch(value);
    if (value.length === 0) {
      setState({
        ...state,
        page: 1
      })
      setActive(false)
    }
  }

  const CategoryItem = useCallback((item) => {
    const { name, slug } = item.item
    const isActive = state.isActiveSlug === slug;
    return (
      <TouchableOpacity key={item.index} style={CATEGORY_CONTAINER(isActive)} onPress={() => filter(slug)}>
        <Text preset="title4" text={name} style={CATEGORY_TEXT(isActive)} colorScheme={colorScheme} />
      </TouchableOpacity>
    )
  }, [colorScheme, filter, state.isActiveSlug])

  const LatestNewsItem = useCallback((item) => {
    const { author, title, image } = item.item
    const img = image ? { uri: `${BASE_URL}/img/cache/large/${image}` } : require("./empty-img.png")
    return (
      <TouchableOpacity activeOpacity={0.6} onPress={onDetail(item.item)}>
        <View style={LATEST_NEWS_CONTAINER}>
          <ImageBackground source={img} style={IMG_BACKGROUND} imageStyle={IMG_STYLE}>
            <Text preset="caption1" text={author.name} style={NEWS_TEXT} colorScheme={colorScheme} />
            <Text preset="display2" text={title} style={NEWS_TEXT} colorScheme={colorScheme} />
          </ImageBackground>
        </View>
      </TouchableOpacity>
    )
  }, [colorScheme, onDetail])

  const CardNews = useCallback((item) => {
    const { author, title, image } = item.item;
    const img = image ? { uri: `${BASE_URL}/img/cache/large/${image}` } : require("./empty-img.png")
    return (
      <TouchableOpacity style={PADDING} activeOpacity={0.6} onPress={onDetail(item.item)}>
        <View style={CARD_CONTAINER}>
          <AutoImage source={img} style={IMG} />
          <View style={NEWS_WRAPPER}>
            <Text preset="title4" numberOfLines={2} ellipsizeMode="tail" text={title} colorScheme={colorScheme} />
            <Text preset="body3" ellipsizeMode="tail" text={author.name} style={AUTHOR_TEXT} colorScheme={colorScheme} />
          </View>
        </View>
      </TouchableOpacity>
    )
  },[colorScheme, onDetail])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundColor={colorScheme === "light" ? color.palette.white : undefined}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
      hideOnBlurred={false}
    >
      <View style={SEARCH_CONTAINER}>
        <SearchBar
          title={isActive ? "Clear" : "Search"}
          placeholder="newsScreen.newsSearchPlaceholder"
          onSubmitEditing={handleSearch}
          onChangeText={handleChangeText}
          onPress={handleSearch}
          value={search}
        />
      </View>
      <LazyRender renderPlaceholder={() => <Text alignSelf="center" mt="72" text="Loading...." colorScheme={colorScheme} />} >
        <View style={CONTAINER}>
          <Text preset="title3" text={label} style={TITLE_TEXT} colorScheme={colorScheme} />
          <FlatList
            contentContainerStyle={LIST}
            data={news}
            keyExtractor={(item, index) => String(index)}
            renderItem={CardNews}
            ListEmptyComponent={() => {
              if (loading) {
                return (
                  <View height={300} justifyContent={"center"} alignItems={"center"}>
                    <JalaLoading />
                  </View>
                )
              }
              return (
                <EmptyState data={categories} searchBy="newsScreen.searchByCategory" onPress={filter} error={error} onRefresh={onRefresh} />
              )
            }}
            ListHeaderComponent={() => {
              if (isActive || news.length === 0) {
                return null
              }
              return (
                <>
                  <FlatList
                    data={latestNews}
                    showsVerticalScrollIndicator={false}
                    keyExtractor={(item, index) => String(index)}
                    renderItem={LatestNewsItem}
                    showsHorizontalScrollIndicator={false}
                    ItemSeparatorComponent={() => (<View style={WIDTH} />)}
                    contentContainerStyle={PADDING}
                    horizontal
                  />
                  <Text preset="title3" tx="newsScreen.category" style={TITLE_TEXT} colorScheme={colorScheme} />
                  <FlatList
                    data={categories}
                    ref={categoryRef}
                    keyExtractor={(item, index) => String(index)}
                    renderItem={CategoryItem}
                    ItemSeparatorComponent={() => (<View width="8px" />)}
                    contentContainerStyle={PADDING}
                    showsHorizontalScrollIndicator={false}
                    horizontal
                  />
                </>
              )
            }}
            onEndReached={next}
            ListFooterComponent={() => {
              if (!loading || state.page === 1) {
                return null
              }
              return (
                <View
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <JalaLoading />
                </View>
              )
            }}
          />
        </View>
      </LazyRender>
    </Screen>
  )
})
